import type { Pinia } from "pinia"
import { defineStore, getActivePinia } from "pinia"
import type {tableStoreContextInput} from "../../@types/tables"
import {TableConstraints} from "../../@types/tables"
import {
    defaultTableValues,
    useTableContextStore,
    useTableFilterStore,
    useTableLimitStore,
    useTablePageStore
} from '../../stores/tableStore'
import {removeItemFromStorage, roundPageLimit} from "./storeHelperComposable"


const useComponentReloadStore = defineStore('componentReload', {
    state: () => ({
        keys: {} as Record<string, number>,
    }),

    actions: {
        init(key: string): void {
            if (this.keys[key] === undefined) {
                this.keys[key] = 0;
            }
        },

        reload(key: string): void {
            this.keys[key] = (this.keys[key] ?? 0) + 1;
        },

        getKey(key: string): number {
            return this.keys[key] ?? 0;
        },
    },
});

class TableController {
    private get activePinia(): Pinia | null {
        return getActivePinia() || null
    }
    public currentPage: number
    public defaultLimit: number
    public currentLimit: number
    public sortOrder: string
    public filters: object
    public persistFilters: boolean
    constructor() {
        this.currentPage = defaultTableValues.currentPage
        this.defaultLimit = defaultTableValues.defaultLimit
        this.currentLimit = this.defaultLimit
        this.sortOrder = 'asc'
        this.filters = {}
        this.persistFilters = true
    }
    private initTable = (id: string) => {
        if (!this.activePinia) {
            const storedTableLimitsKey = 'storedTableLimits';
            const storedTablePagesKey = 'storedTablePages';
            const storedTableContextsKey = 'storedTableContexts';

            const storageKeys = [
                storedTableLimitsKey,
                storedTablePagesKey,
                storedTableContextsKey
            ];

            const hasExistingStorage = storageKeys.some(key =>
                localStorage.getItem(key) || sessionStorage.getItem(key)
            );

            if (hasExistingStorage) {
                return;
            }

            const options = {
                [id]: {
                    defaultLimit: this.defaultLimit,
                    currentPageLimit: this.currentPage,
                    sortOrder: this.sortOrder,
                }
            };

            const pageData = {
                [id]: {
                    currentPage: this.currentPage
                }
            };

            const contextData = {
                id,
                parentPath: window.location.pathname,
                childPath: null,
                context: 'table'
            };

            localStorage.setItem(storedTableLimitsKey, JSON.stringify(options));
            sessionStorage.setItem(storedTablePagesKey, JSON.stringify(pageData));
            sessionStorage.setItem(storedTableContextsKey, JSON.stringify(contextData));
            return;
        }

        const tableLimitStore = useTableLimitStore();
        const pageStore = useTablePageStore();
        const storeContext = useTableContextStore();

        storeContext.initContext({
            id,
            path: window.location.pathname,
            childPath: null,
        });
        tableLimitStore.initLimit(id);
        pageStore.initPage(id);
    }
    initTables = (...ids: string[]) => {
        ids.forEach((id) => this.initTable(id))
    }
    initFilters = (id?: string | null) => {
        if (!this.activePinia) {
            const storedTableFiltersKey = 'storedTableFilters';
            const hasExistingStorage = sessionStorage.getItem(storedTableFiltersKey)

            if (hasExistingStorage) {
                return
            }

            const filterData = {
                filters: this.filters
            };

            sessionStorage.setItem(storedTableFiltersKey, JSON.stringify(filterData));
            return
        }

        const filterStore = useTableFilterStore();
        filterStore.initFilters(id);
    }

    private getComponentReloadStore = () => {
        if (!this.activePinia) {
            return null;
        }

        return useComponentReloadStore();
    }

    initComponentReload = (key: string): void => {
        const store = this.getComponentReloadStore();

        if (!store) {
            return;
        }

        store.init(key);
    }

    reloadComponentGroup = (key: string): void => {
        const store = this.getComponentReloadStore();

        if (!store) {
            return;
        }

        store.reload(key);
    }

    getComponentReloadKey = (key: string): number => {
        const store = this.getComponentReloadStore();

        if (!store) {
            return 0;
        }

        return store.getKey(key);
    }

    setLimit = (id: string, newLimit: number, type: TableConstraints = TableConstraints.Current) => {
        if (!this.activePinia) {
            const localData = {
                [id]: {
                    currentPageLimit: roundPageLimit(newLimit)
                }
            }
            localStorage.setItem('storedTableLimits', JSON.stringify(localData))
            return
        }

        const perPage = useTableLimitStore()
        perPage.initLimit(id)
        perPage.setLimit(id, roundPageLimit(newLimit), type)
    }
    setPage = (id: string, num: number) => {
        if (!this.activePinia) {
            const localData = {
                [id]: {
                    currentPage: num
                }
            }
            sessionStorage.setItem('storedTablePages', JSON.stringify(localData))
            return
        }

        const pageStore = useTablePageStore()
        pageStore.initPage(id)
        pageStore.setPage(id, num)
    }
    getCurrentPage = (id: string, type: TableConstraints = TableConstraints.Current) => {
        if (!this.activePinia && sessionStorage.getItem('storedTablePages')) {
            const storedPage = JSON.parse(sessionStorage.getItem('storedTablePages') ?? '')
            return storedPage[id]?.currentPage
        }

        const pageStore = useTablePageStore()
        return pageStore.getPage(id, type) || this.currentPage
    }
    getLimit = (id: string, type: TableConstraints = TableConstraints.Current) => {
        const limitKey = type === TableConstraints.Default ? 'defaultLimit' : 'currentPageLimit';

        if (!this.activePinia && sessionStorage.getItem('storedTableLimits')) {
            const storedLimit = JSON.parse(sessionStorage.getItem('storedTableLimits') ?? '')
            return roundPageLimit(storedLimit[id]?.[limitKey] || this.currentLimit)
        }

        const tableLimitStore = useTableLimitStore()
        tableLimitStore.initLimit(id)

        return roundPageLimit(tableLimitStore?.getLimit(id, type) || this.currentLimit)
    }
    getShouldPersistFilters = () => {
        if (!this.activePinia && sessionStorage.getItem('storedTableFilters')) {
            const storedPersist = JSON.parse(sessionStorage.getItem('storedTableFilters') ?? '')
            return storedPersist.persist
        }

        const filterStore = useTableFilterStore()

        return filterStore?.getShouldPersistFilters() || this.persistFilters
    }
    setShouldPersistFilters = (persist: boolean) => {
        this.persistFilters = persist
        if (!this.activePinia) {
            sessionStorage.setItem('storedTableFilters', JSON.stringify({ persist, 'filters': {} }))
            return
        }

        const filterStore = useTableFilterStore()
        filterStore.setShouldPersistFilters(persist)
    }
    addFilters = (id: string, filters: object) => {
        const persist = this.getShouldPersistFilters()
        if (!this.persistFilters && !persist) {
            return
        }

        if (!this.activePinia) {
            const localData = {
                persist: this.persistFilters,
                filters: {
                    [id]: filters
                }
            }
            sessionStorage.setItem('storedTableFilters', JSON.stringify(localData))
            return
        }

        const filterStore = useTableFilterStore()
        filterStore.initFilters(id)
        filterStore.setFilters(id, filters)
    }
    useComponentReloadStore = defineStore('componentReload', {
        state: () => ({
            keys: {} as Record<string, number>,
        }),

        getters: {
            getKey: (state) => {
                return (key: string): number => state.keys[key] ?? 0;
            },
        },

        actions: {
            init(key: string): void {
                if (this.keys[key] === undefined) {
                    this.keys[key] = 0;
                }
            },

            reload(key: string): void {
                this.keys[key] = (this.keys[key] ?? 0) + 1;
            },
        },
    })
    clearFilters = (id?: string | null, tableId?: string | null) => {
        this.persistFilters = this.getShouldPersistFilters()
        if (!this.activePinia) {
            removeItemFromStorage('storedTableFilters', id || null)
            return
        }

        const filterStore = useTableFilterStore()
        filterStore.deleteFilters(id)

        if (tableId) {
            this.setLimit(tableId, this.defaultLimit)
        }

        this.setShouldPersistFilters(this.persistFilters)
    }
    getFilters = (id: string) => {
        if (!this.persistFilters) {
            return {}
        }
        if (!this.activePinia && sessionStorage.getItem('storedTableFilters')) {
            const storedFilters = JSON.parse(sessionStorage.getItem('storedTableFilters') ?? '')
            return storedFilters.filters[id] || {}
        }

        const filterStore = useTableFilterStore()
        filterStore.initFilters(id)
        return filterStore.getFilters(id)
    }
    deleteStores = (id: string, all?: boolean) => {
        if (!this.activePinia) {
            const keys = ['storedTableLimits', 'storedTablePages', 'storedTableFilters', 'storedTableContexts']
            keys.forEach((key) => {
                if (localStorage.getItem(key) || sessionStorage.getItem(key)) {
                    removeItemFromStorage(key, all || key === 'storedTableContexts' ? null : id, key !== 'storedTableLimits')
                }
            });
            return
        }

        const tableLimitStore = useTableLimitStore()
        const pageStore = useTablePageStore()
        const filterStore = useTableFilterStore()
        const storeContext = useTableContextStore()

        tableLimitStore.initLimit(id)
        pageStore.initPage(id)
        filterStore.initFilters(id)

        storeContext.$reset()

        if (all) {
            tableLimitStore.$reset()
            pageStore.$reset()
            filterStore.$reset()
            return
        }

        tableLimitStore.deleteLimit(id)
        pageStore.deletePages(id)
        filterStore.deleteFilters(id)
    }
    getStoreContext = (id: string) => {
        if (!this.activePinia && sessionStorage.getItem('storedTableContexts')) {
            const storedContexts = JSON.parse(sessionStorage.getItem('storedTableContexts') ?? '')
            return storedContexts[id] || null
        }

        const tableContextStore = useTableContextStore()
        return tableContextStore.getContext() || null
    }
    setStoreContext = (id: string, ctx: tableStoreContextInput = {}) => {
        const tempCtx = {
            ...ctx,
            id,
            path: ctx.path ?? window.location.pathname,
            childPath: ctx.childPath ?? null,
        }

        if (!this.activePinia) {
            const localData = {
                context: tempCtx
            }
            sessionStorage.setItem('storedTableContexts', JSON.stringify(localData))
            return
        }

        const tableContextStore = useTableContextStore()
        tableContextStore.setContext(tempCtx)
    }
}

export default TableController
